
import { ValidationObserver } from 'vee-validate'
import availableFormFields from './availableFormFields'
import BaseSheet from '~/components/base/BaseSheet'
import formWatcher from '~/services/formWatcher/formWatcher'
import formWatcherRo from '~/services/formWatcher/formWatcherRo'
import uniqIdMixin from '~/mixins/uniq-id'
import countryCode from '~/mixins/countryCode'

export default {
  name: 'BaseFormBuilder',
  components: {
    ValidationObserver,
  },

  mixins: [
    uniqIdMixin,
    countryCode,
  ],

  props: {
    addLoader: {
      type: Boolean,
      default: true,
    },

    value: {
      type: Object,
      required: true,
    },

    schema: {
      type: Object,
      required: true,
    },

    mutation: {
      type: String,
      default: null,
    },

    variables: {
      type: Object,
      default: null,
    },

    withSheet: {
      type: Boolean,
      required: false,
    },

    mode: {
      type: String,
      default: 'default',
    },

    cancel: {
      type: Boolean,
      default: false,
    },

    cancelText: {
      type: String,
      default: 'Cancel',
    },

    submitText: {
      type: String,
      default: 'Save',
    },

    isConvertToUnderscore: {
      type: Boolean,
      default: true,
    },

    graphqlOperationName: {
      type: String,
      default: '',
    },

    isFirstPrevBtnEnabled: {
      type: Boolean,
      default: false,
    },

    questionnaireNavColor: {
      type: Object,
      default: () => {},
    },

    isHeaderVisible: {
      type: Boolean,
      default: true,
    },

    requiredFieldsText: {
      type: String,
      default: 'Required fields',
    },

  },

  data () {
    return {
      page: 0,
      reverse: false,
    }
  },

  computed: {
    sectionComponent () {
      return this.withSheet ? BaseSheet : 'div'
    },

    getSectionCount () {
      return Object.keys(this.schema.sections).length - 1
    },
  },

  watch: {
    value (value, prevValue) {
      this.schema.watch?.forEach(async (watcher) => {
        const [[field, callback]] = Object.entries(watcher)
        const currentFormWatcher = this.isCountryRomania ? formWatcherRo : formWatcher

        if (value[field] !== prevValue[field] && callback in currentFormWatcher) {
          await currentFormWatcher[callback].call(this, this.value, this.schema)
          this.$emit('input', this.value)
        }
      })
    },
  },

  mounted () {
    this.schema.watch?.forEach(async (watcher) => {
      const [callback] = Object.values(watcher)
      const currentFormWatcher = this.isCountryRomania ? formWatcherRo : formWatcher

      if (callback in currentFormWatcher) {
        await currentFormWatcher[callback].call(this, this.value, this.schema)
      }
    })
  },

  methods: {
    getFormComponent (component) {
      if (typeof component === 'function' || typeof component === 'object') {
        return component
      }

      const componentFound = availableFormFields[component]

      if (!componentFound) {
        throw new Error(`You have not specified following component - ${component}`)
      }

      return componentFound
    },

    onInput (value, name) {
      this.$emit('input', {
        ...this.value,
        [name]: value,
      })
    },

    onGroupedInput (groupedValues) {
      this.$emit('input', {
        ...this.value,
        ...groupedValues,
      })
    },

    checkConditionsForSection (showSectionWhen) {
      if (showSectionWhen) {
        return showSectionWhen.every((condition) => {
          if ('equals' in condition) {
            return this.value[condition.model] === condition.equals
          }

          return true
        })
      }
      return true
    },

    checkConditions (element) {
      if (element) {
        const { showElementWhen = [] } = element

        return showElementWhen.every((condition) => {
          if ('equals' in condition) {
            return this.value[condition.model] === condition.equals
          }
          if ('filled' in condition) {
            const isFilled = !!this.value[condition.model]

            return condition.filled ? isFilled : !isFilled
          }
          return true
        })
      }
      return false
    },

    checkRequiredNotice (elements) {
      const fields = Object.values(elements)

      return fields.length > 3 && fields.find(f => f.rules?.includes('required'))
    },

    async scrollToFormTop () {
      await new Promise((resolve) => {
        const elementToScroll = document.querySelector('.form-scroll-top') || this.$refs.form.$el

        const finished = setTimeout(() => {
          document.removeEventListener('scroll', checkPos)
          resolve()
        }, 700)

        const checkPos = () => {
          if (!elementToScroll) {
            resolve()
            return
          }

          const { top } = elementToScroll.getBoundingClientRect()

          if (top >= 0) {
            clearTimeout(finished)
            document.removeEventListener('scroll', checkPos)
            resolve()
          }
        }

        document.addEventListener('scroll', checkPos)

        if (elementToScroll) {
          elementToScroll.scrollIntoView({ behavior: 'smooth' })
          checkPos()
        } else {
          resolve()
        }
      })
    },

    async onPrevPage () {
      if (this.page === 0) {
        this.$emit('first-page-prev')
      }

      if (this.page > 0) {
        await this.scrollToFormTop()
        this.page--
      }

      this.$emit('page-number', this.page)
    },

    async onNextPage (validate) {
      const valid = await validate()

      if (valid) {
        await this.scrollToFormTop()
        this.page++
      } else {
        this.$refs?.form?.onValidationFailed()
      }

      this.$emit('page-number', this.page)
    },

    onSubmit () {
      this.$emit('submit')
    },

    onSuccess (data) {
      this.$emit('success', data)
    },

    onError (message) {
      this.$emit('error', message)
    },

    setErrors (errors) {
      this.$refs.form.setErrors(errors)
    },
  },

}
